'use client';

import React, { useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { BuildingsIcon, MagnifyingGlassIcon, PencilSimpleIcon, PlusIcon } from '@phosphor-icons/react';
import { Card, CardBody, CardHeader } from '@heroui/card';
import { Button } from '@heroui/button';
import { Input } from '@heroui/input';
import { Spinner } from '@heroui/spinner';
import { Modal, ModalContent, ModalHeader, ModalBody, useDisclosure } from '@heroui/modal';
import { AuthContext, ToastContext } from '@/app/contexts';
import { ToastLevel } from '@/components/ui/toast';
import { useCSM } from '@/contexts/CSMContext';
import { listTenants, createTenant, getTenant, updateTenant } from '@/lib/admin-tenants';
import { listActiveCSMs } from '@/lib/admin-csm-access';
import { getTokenForHeimdallAdmin } from '@/lib/heimdall-admin-token';
import { CreateTenantForm } from './CreateTenantForm';
import { EditTenantForm } from './EditTenantForm';
import type { ActiveCSMUserListResponse, CreateTenantRequest, CSMUserSummaryDTO, TenantDTO, UpdateTenantRequest } from '@/shared/types';
export function TenantsTab() {
  const {
    idToken
  } = useContext(AuthContext);
  const toastContext = useContext(ToastContext);
  const {
    isSuperAdmin,
    canManageOrgs,
    isLoaded
  } = useCSM();
  const [tenants, setTenants] = useState<TenantDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const {
    isOpen,
    onOpen,
    onOpenChange
  } = useDisclosure();
  const [activeCsmList, setActiveCsmList] = useState<CSMUserSummaryDTO[]>([]);
  const [editTenantId, setEditTenantId] = useState<string | null>(null);
  const [editTenant, setEditTenant] = useState<TenantDTO | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const didSaveRef = useRef(false);
  const fetchList = useCallback(async () => {
    const token = await getTokenForHeimdallAdmin(idToken ?? null);
    if (!token) {
      setLoading(false);
      setError('Employee token unavailable. Switch tenant or refresh the page, then try again.');
      return;
    }
    let tenantFetchSucceeded = false;
    try {
      setLoading(true);
      setError(null);
      const res = await listTenants(token, 0, 1000);
      setTenants(res.tenants ?? []);
      setTotal(res.total ?? 0);
      tenantFetchSucceeded = true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
    if (tenantFetchSucceeded) {
      try {
        const result: ActiveCSMUserListResponse = await listActiveCSMs(token);
        const sorted = [...(result?.csm_list ?? [])].sort((a, b) => (a.email ?? '').toLowerCase().localeCompare((b.email ?? '').toLowerCase()));
        setActiveCsmList(sorted);
      } catch {
        // CSM list is supplementary — a failure here should not block the tenant list
      }
    }
  }, [idToken]);

  // Refreshes only the CSM access lists in the background without touching loading/error state.
  // Used when the edit modal is dismissed without saving, since CSM grants/revokes inside the
  // modal are persisted immediately and the tenant cards need up-to-date access lists.
  const refreshCsmListSilently = useCallback(async () => {
    const token = await getTokenForHeimdallAdmin(idToken ?? null);
    if (!token) return;
    try {
      const result: ActiveCSMUserListResponse = await listActiveCSMs(token);
      const sorted = [...(result?.csm_list ?? [])].sort((a, b) => (a.email ?? '').toLowerCase().localeCompare((b.email ?? '').toLowerCase()));
      setActiveCsmList(sorted);
    } catch {
      // Background refresh — failures are silent so the visible tenant list is unaffected
    }
  }, [idToken]);
  useEffect(() => {
    if (isLoaded && (isSuperAdmin || canManageOrgs) && idToken) {
      fetchList();
    }
  }, [isLoaded, isSuperAdmin, canManageOrgs, idToken, fetchList]);
  const handleCreateSuccess = (tenant: TenantDTO) => {
    fetchList();
    if (toastContext?.setToast && toastContext?.setToastOpen) {
      toastContext.setToast({
        setOpen: toastContext.setToastOpen,
        title: 'Tenant created',
        description: tenant.tenant_name,
        level: ToastLevel.SUCCESS,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 3000
      });
      toastContext.setToastOpen(true);
    }
  };
  const handleSubmitCreate = async (data: CreateTenantRequest) => {
    const token = await getTokenForHeimdallAdmin(idToken ?? null);
    if (!token) throw new Error('Not authenticated');
    return createTenant(token, data);
  };
  useEffect(() => {
    if (!editTenantId) return;
    let cancelled = false;
    setEditTenant(null);
    setEditError(null);
    setEditLoading(true);
    getTokenForHeimdallAdmin(idToken ?? null).then(token => {
      if (cancelled) return;
      if (!token) {
        setEditError('Not authenticated. Please refresh and try again.');
        return;
      }
      return getTenant(token, editTenantId).then(t => {
        if (!cancelled) setEditTenant(t);
      }).catch(err => {
        if (!cancelled) setEditError(err instanceof Error ? err.message : 'Failed to load tenant');
      });
    }).finally(() => {
      if (!cancelled) setEditLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [editTenantId, idToken]);
  const handleEditSuccess = (tenant: TenantDTO) => {
    didSaveRef.current = true;
    setEditTenantId(null);
    setEditTenant(null);
    setEditError(null);
    fetchList();
    if (toastContext?.setToast && toastContext?.setToastOpen) {
      toastContext.setToast({
        setOpen: toastContext.setToastOpen,
        title: 'Tenant updated',
        description: tenant.tenant_name,
        level: ToastLevel.SUCCESS,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 3000
      });
      toastContext.setToastOpen(true);
    }
  };
  const handleSubmitEdit = async (tenantId: string, data: Parameters<typeof updateTenant>[2]) => {
    const token = await getTokenForHeimdallAdmin(idToken ?? null);
    if (!token) throw new Error('Not authenticated');
    return updateTenant(token, tenantId, data);
  };
  const filteredTenants = useMemo(() => {
    if (!searchQuery.trim()) return tenants;
    const q = searchQuery.trim().toLowerCase();
    return tenants.filter(t => t.tenant_name?.toLowerCase().includes(q) || t.tenant_code?.toLowerCase().includes(q) || t.contact_email?.toLowerCase().includes(q));
  }, [tenants, searchQuery]);
  if (!isLoaded || !(isSuperAdmin || canManageOrgs)) {
    return <Card classNames={{
      base: 'bg-white/5 border-white/10',
      body: 'text-center py-12'
    }}>
        <CardBody>
          <Spinner size="lg" color="secondary" />
          <p className="text-gray-300 mt-4">Loading...</p>
        </CardBody>
      </Card>;
  }
  return <>
      <div className="flex flex-col gap-4">
        {loading ? <Card classNames={{
        base: 'bg-white/5 border-white/10',
        body: 'text-center flex flex-col items-center justify-center gap-2 py-12'
      }}>
            <CardBody>
              <Spinner size="lg" color="secondary" variant="gradient" />
              <p className="text-gray-300">Loading tenants...</p>
            </CardBody>
          </Card> : error ? <Card className="w-full bg-white/5 border-white/10">
            <CardBody className="flex flex-col items-center justify-center text-center">
              <p role="alert" className="text-red-400 mb-4">
                Error loading tenants: {error}
              </p>
              <Button color="primary" onPress={fetchList} className="w-fit text-white hover:bg-white/10 cursor-pointer">
                Retry
              </Button>
            </CardBody>
          </Card> : tenants.length === 0 ? <Card className="max-w-full overflow-visible bg-white/5 border-white/10">
            <CardBody className="text-center">
              <BuildingsIcon size={64} className="text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Tenants</h3>
              <p className="text-gray-300 mb-6">
                Create your first tenant to get started.
              </p>
              <Button color="primary" onPress={onOpen} startContent={<PlusIcon size={20} />} className="cursor-pointer text-white">
                Create Tenant
              </Button>
            </CardBody>
          </Card> : <Card classNames={{
        base: 'box-border bg-white/5 border-white/10 p-4 rounded-lg h-[calc(100vh-300px)] flex flex-col',
        header: 'box-border flex justify-between items-center bg-white/5 p-4 rounded-lg',
        body: 'box-border pt-6 overflow-y-auto min-h-0 flex-1'
      }}>
            <CardHeader className="flex justify-between items-center">
              <span className="text-white font-medium">Tenants ({total})</span>
              <Button color="primary" size="sm" onPress={onOpen} startContent={<PlusIcon size={18} />} className="cursor-pointer text-white">
                Create Tenant
              </Button>
            </CardHeader>
            <CardBody>
              <Input aria-label="Search tenants by name, code, or email" placeholder="Search by name, code, or email..." value={searchQuery} onValueChange={setSearchQuery} startContent={<MagnifyingGlassIcon size={18} className="text-slate-400" />} classNames={{
            input: 'text-white placeholder-slate-400',
            inputWrapper: 'bg-slate-700/50 border border-slate-600 rounded-lg data-[hover=true]:bg-slate-600/50 group-data-[focus=true]:border-purple-500 mb-4',
            label: 'text-slate-300'
          }} />
              <div className="grid grid-cols-1 gap-4">
                {filteredTenants.length === 0 ? <p className="text-slate-400 py-6 text-center">
                    {searchQuery.trim() ? 'No tenants match your search.' : 'No tenants.'}
                  </p> : filteredTenants.map(tenant => <div key={tenant.id} role="button" tabIndex={0} onClick={() => setEditTenantId(tenant.id)} onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setEditTenantId(tenant.id);
              }
            }} className="block p-6 rounded-lg border border-purple-700/50 bg-slate-800/50 hover:bg-slate-700/50 hover:border-purple-500/50 transition-all shadow-lg cursor-pointer">
                    <div className="flex gap-4 items-start">

                      {/* ── Left: tenant info ─────────────────────────────── */}
                      <div className="min-w-0 basis-[55%] shrink-0">
                        <div className="flex justify-between items-start gap-2 mb-2">
                          <div className="min-w-0">
                            <h3 className="text-lg font-semibold text-purple-300">{tenant.tenant_name}</h3>
                            {tenant.organization?.name && <h4 className="text-sm font-medium text-purple-300/70">{tenant.organization.name}</h4>}
                          </div>
                          <Button size="sm" variant="bordered" onPress={() => setEditTenantId(tenant.id)} onClick={e => e.stopPropagation()} className="shrink-0 border-white/20 text-white hover:bg-white/10" aria-label={`Edit ${tenant.tenant_name}`} startContent={<PencilSimpleIcon size={16} />}>
                            Edit
                          </Button>
                        </div>
                        <p className="text-sm text-slate-400 mb-1">
                          Status: <span className={`font-medium ${tenant.status === 'active' ? 'text-green-400' : 'text-red-400'}`}>{tenant.status}</span>
                        </p>
                        {tenant.tenant_code && <p className="text-sm text-slate-400 mb-1">Tenant Friendly Name: {tenant.tenant_code}</p>}
                        <p className="text-sm text-slate-400 mb-1">Timezone: {tenant.timezone ?? 'N/A'}</p>
                        {tenant.cognito_domain && <p className="text-sm text-slate-400">Cognito Domain: {tenant.cognito_domain}</p>}
                      </div>

                      {/* ── Divider ────────────────────────────────────────── */}
                      <div className="w-px self-stretch bg-purple-700/30" />

                      {/* ── Right: CSM access list ─────────────────────────── */}
                      <div className="basis-[40%] shrink-0 min-w-0 flex flex-col gap-1" onClick={e => e.stopPropagation()} role="presentation">
                        <p className="text-xs font-semibold text-purple-300 uppercase tracking-wider mb-1">
                          CSM Access
                        </p>
                        {/* Scrollable list — wire up data here */}
                        <ul className="overflow-y-auto max-h-60 space-y-1 custom-scrollbar2">
                          {(() => {
                      const csms = activeCsmList.filter(csm => csm.is_active && csm.accessible_tenants.includes(tenant.id));
                      return csms.length === 0 ? <li className="text-xs text-slate-500 italic text-center py-2">None</li> : csms.map(csm => <li key={csm.email} className="text-sm text-slate-100 truncate px-1 py-1">
                                    {csm.email}
                                  </li>);
                    })()}
                        </ul>
                      </div>

                    </div>
                  </div>)}
              </div>
            </CardBody>
          </Card>}
      </div>

      <Modal isOpen={isOpen} onOpenChange={onOpenChange} classNames={{
      base: 'bg-slate-900 border border-purple-700/50 rounded-xl shadow-2xl',
      header: 'border-b border-purple-500/20 text-purple-300',
      body: 'text-white',
      closeButton: 'text-purple-300 hover:text-white hover:bg-purple-500/20'
    }} shouldCloseOnInteractOutside={element => {
      // Keep modal open when clicking react-select menu (portaled to body)
      const el = element as HTMLElement;
      return !el.closest?.('.react-select__menu');
    }} data-sentry-element="Modal" data-sentry-source-file="TenantsTab.tsx">
        <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="TenantsTab.tsx">
          {onClose => <>
              <ModalHeader className="flex flex-col gap-1 text-purple-300 text-xl font-bold">
                Create Tenant
              </ModalHeader>
              <ModalBody>
                <CreateTenantForm idToken={idToken} onSubmit={handleSubmitCreate} onSuccess={tenant => {
              handleCreateSuccess(tenant);
              onClose();
            }} onCancel={onClose} />
              </ModalBody>
            </>}
        </ModalContent>
      </Modal>

      <Modal isOpen={editTenantId != null} size="4xl" onOpenChange={open => {
      if (!open) {
        setEditTenantId(null);
        setEditTenant(null);
        setEditError(null);
        if (didSaveRef.current) {
          didSaveRef.current = false;
        } else {
          refreshCsmListSilently();
        }
      }
    }} classNames={{
      base: 'bg-slate-900 border border-purple-700/50 rounded-xl shadow-2xl',
      header: 'border-b border-purple-500/20 text-purple-300',
      body: 'text-white',
      closeButton: 'text-purple-300 hover:text-white hover:bg-purple-500/20'
    }} shouldCloseOnInteractOutside={element => {
      const el = element as HTMLElement;
      return !el.closest?.('.react-select__menu');
    }} data-sentry-element="Modal" data-sentry-source-file="TenantsTab.tsx">
        <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="TenantsTab.tsx">
          {onClose => <>
              <ModalHeader className="flex flex-col gap-1 text-purple-300 text-xl font-bold">
                Edit Tenant
              </ModalHeader>
              <ModalBody>
                {editLoading && <div className="flex flex-col items-center justify-center gap-2 py-8">
                    <Spinner size="lg" color="secondary" />
                    <p className="text-gray-300">Loading tenant...</p>
                  </div>}
                {!editLoading && editError && <div className="space-y-4">
                    <p role="alert" className="text-red-400">{editError}</p>
                    <Button onPress={onClose} className="text-white">Close</Button>
                  </div>}
                {!editLoading && !editError && editTenant && idToken && <EditTenantForm tenant={editTenant} idToken={idToken} onSubmit={handleSubmitEdit} onSuccess={tenant => {
              handleEditSuccess(tenant);
              onClose();
            }} onCancel={onClose} />}
              </ModalBody>
            </>}
        </ModalContent>
      </Modal>
    </>;
}